import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TableColType } from '@shared/enums/table.enum';
import { IActivity } from '@shared/interfaces/activity.interface';
import { ICandidate } from '@shared/interfaces/candidate.interface';
import { ITableCol } from '@shared/interfaces/table.col.interface';
import { Pagination } from '@shared/models/pagination.model';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { LoggerService } from '@shared/services/logger.service';
import { PaginationCandidatesActivitiesService } from '@shared/services/pagination/candidates/pagination.candidates.activities.service';
import { Broadcaster, NgXCable } from '@shared/services/action-cable.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-sidebar-candidate-activities',
    templateUrl: './sidebar.candidate.activities.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SidebarCandidateActivitiesComponent implements OnInit, OnDestroy {
  @Input() public candidate: ICandidate;

  public page: Pagination<IActivity>;
  public rows: IActivity[] = [];
  public cols: Partial<ITableCol>[] = [
    {
      headerText: 'TABLES.HEADERS.DATE',
      type: TableColType.Ago,
      selector: 'created_at'
    },
    {
      headerText: 'TABLES.HEADERS.DESCRIPTION',
      type: TableColType.Translatable,
      selector: 'description'
    }
  ];

  private readonly constructorName: string = String(this.constructor.name);
  private readonly channel = String('CandidatesChannel');
  private _broadcastSubscription: Subscription;
  private _paginationSubscription: Subscription;
  private _cableSubscriptions: any[] = [];

  constructor(
    private readonly _broadcaster: Broadcaster,
    private readonly _commonEnvironments: CommonEnvironmentsService,
    private readonly _logger: LoggerService,
    private readonly _ngcable: NgXCable,
    private readonly _paginationCandidatesActivities: PaginationCandidatesActivitiesService
  ) {}

  public get pagination(): PaginationCandidatesActivitiesService {
    return this._paginationCandidatesActivities;
  }

  ngOnInit(): void {
    this.getActivities();

    this._ngcable.connect(this._commonEnvironments.ENDPOINTS_SOCKET);
    this._cableSubscriptions = [
      this._ngcable.create({ channel: this.channel, room: `${this.candidate?.id}/activities` })
    ];

    this._broadcastSubscription = this._broadcaster.on(this.channel).subscribe((res: any) => {
      if (res.type === 'candidate_activities_viewed') {
        this._paginationCandidatesActivities.reloadPage(
          this._paginationCandidatesActivities.currentPage
        );
      }
    });
  }

  ngOnDestroy(): void {
    this._broadcastSubscription?.unsubscribe();
    this._paginationSubscription?.unsubscribe();
    this._ngcable?.unsubscribe(this._cableSubscriptions);
  }

  public getActivities() {
    // Set the filter synchronously so <app-pagination>'s ngOnInit requests
    // the first page for the right candidate
    this._paginationCandidatesActivities.setFilter({
      key: 'candidateId',
      value: this.candidate?.id
    });

    const url = `GET /candidates/${this.candidate?.id}/activities`;
    this._paginationSubscription = this._paginationCandidatesActivities.page.subscribe(
      (page: Pagination<IActivity>) => {
        this._logger.info(this.constructorName, url, page);

        this.page = page;
        this.rows = page.entries;
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
      }
    );

    this._paginationCandidatesActivities.fullReload();
  }
}
